import React, { useEffect } from 'react';
import { useGame } from '../context/GameContext';
import { Zap, Heart, Swords, Shield, ChevronRight, Trophy, Skull } from 'lucide-react';

// v2.4 POST-COMBAT PILE SUMMARY — quick brutalist recap between combat end and reward pick.
// Auto-forwards after 6 seconds if the player doesn't click CONTINUE (respectful of speedrunners).

export default function CombatRecap() {
  const { pendingRecap, dismissRecap } = useGame();

  useEffect(() => {
    if (!pendingRecap) return;
    const t = setTimeout(dismissRecap, 8000); // gentle auto-advance
    const onKey = (e) => {
      if (['Enter', ' ', 'Escape'].includes(e.key)) { e.preventDefault(); dismissRecap(); }
    };
    window.addEventListener('keydown', onKey);
    return () => { clearTimeout(t); window.removeEventListener('keydown', onKey); };
  }, [pendingRecap, dismissRecap]);

  if (!pendingRecap) return null;
  const r = pendingRecap;
  const isBoss = r.kind === 'boss' || r.kind === 'midboss';
  const isElite = r.kind === 'elite';
  const hpPct = r.maxHp > 0 ? Math.max(0, Math.min(1, r.finalHp / r.maxHp)) : 0;

  const stats = [
    { key: 'turns', label: 'TURNS', v: r.turns, icon: <Zap size={14}/> },
    { key: 'cards', label: 'CARDS PLAYED', v: r.cardsPlayed, icon: <Swords size={14}/> },
    { key: 'dmg', label: 'DMG DEALT', v: r.dmgDealt, icon: <Swords size={14}/>, hi: r.dmgDealt >= 100 },
    { key: 'taken', label: 'DMG TAKEN', v: r.dmgTaken, icon: <Shield size={14}/>, warn: r.dmgTaken >= 30 },
    { key: 'biggest', label: 'BIGGEST HIT', v: r.biggestHit, icon: <Swords size={14}/>, hi: r.biggestHit >= 40 },
    { key: 'exhaust', label: 'EXHAUSTED', v: r.exhausted, icon: <Skull size={14}/> },
  ];

  return (
    <div className="min-h-screen w-full px-6 py-6 flex flex-col items-center justify-center gap-4" data-testid="combat-recap-screen">
      <div className="text-center">
        <div className="text-[10px] uppercase tracking-[0.4em] text-[color:var(--text-dim)]">
          FLOOR {r.floor} · {isBoss ? 'BOSS' : isElite ? 'ELITE' : 'COMBAT'} DEFEATED
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-black uppercase mt-1" style={{ color: isBoss ? 'var(--pink)' : isElite ? 'var(--red)' : 'var(--neon)' }} data-testid="recap-heading">
          VICTORY
        </h1>
      </div>

      {/* Enemy sprite + name */}
      <div className="flex items-center gap-3" data-testid="recap-enemy">
        <div className="text-5xl">{r.enemySprite}</div>
        <div>
          <div className="text-[10px] uppercase font-mono text-[color:var(--text-dim)]">SLAIN</div>
          <div className="font-display font-black text-xl uppercase text-[color:var(--red)]">{r.enemyName}</div>
        </div>
      </div>

      {/* Stat grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-w-2xl w-full" data-testid="recap-stats">
        {stats.map(st => (
          <div
            key={st.key}
            className="brutal-border-dim p-3 flex flex-col gap-1"
            data-testid={`recap-stat-${st.key}`}
            style={{
              borderColor: st.hi ? 'var(--neon)' : st.warn ? 'var(--red)' : '#333',
              boxShadow: st.hi ? '3px 3px 0 var(--neon)' : st.warn ? '3px 3px 0 var(--red)' : 'none',
            }}
          >
            <div className="text-[9px] uppercase tracking-widest text-[color:var(--text-dim)] flex items-center gap-1">
              {st.icon} {st.label}
            </div>
            <div className="font-display font-black text-2xl" style={{ color: st.hi ? 'var(--neon)' : st.warn ? 'var(--red)' : 'var(--text)' }}>
              {st.v}
            </div>
          </div>
        ))}
      </div>

      {/* HP bar + gold */}
      <div className="max-w-2xl w-full flex flex-col sm:flex-row gap-3 items-stretch">
        <div className="brutal-border-dim p-3 flex-1" data-testid="recap-hp">
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-dim)] flex items-center gap-1 mb-1">
            <Heart size={12}/> ENDING HP
          </div>
          <div className="w-full h-3 bg-[#111] relative overflow-hidden">
            <div
              className="absolute inset-y-0 left-0"
              style={{
                width: `${hpPct * 100}%`,
                background: hpPct > 0.5 ? 'var(--neon)' : hpPct > 0.25 ? '#ffd700' : 'var(--red)',
              }}
            />
          </div>
          <div className="text-xs font-mono mt-1" style={{ color: hpPct > 0.5 ? 'var(--neon)' : hpPct > 0.25 ? '#ffd700' : 'var(--red)' }}>
            {r.finalHp} / {r.maxHp}
          </div>
        </div>
        {r.gold > 0 && (
          <div className="brutal-border-dim p-3 flex items-center gap-3" data-testid="recap-gold" style={{ borderColor: '#ffd700', boxShadow: '3px 3px 0 #ffd700' }}>
            <Trophy size={20} className="text-[#ffd700]"/>
            <div>
              <div className="text-[9px] uppercase tracking-widest text-[color:var(--text-dim)]">GOLD EARNED</div>
              <div className="font-display font-black text-2xl text-[#ffd700]">+{r.gold}</div>
            </div>
          </div>
        )}
      </div>

      <button
        onClick={dismissRecap}
        className="brutal-btn text-lg px-8 py-3 mt-2 flex items-center gap-2"
        data-testid="recap-continue"
        style={{ borderColor: 'var(--neon)', boxShadow: '4px 4px 0 var(--neon)' }}
      >
        CONTINUE <ChevronRight size={18}/>
      </button>
      <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-dim)]">
        Press ENTER / SPACE / ESC · Auto-advance in 8s
      </div>
    </div>
  );
}
